'use client'

import { cn } from '@brew/ui/cn'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { type ComponentProps, type ReactNode, useId } from 'react'
import { Spinner } from '@/components/ui/spinner'
import {
  AlertTriangleIcon,
  CheckIcon,
  ChevronRightIcon,
  InfoIcon,
  type LucideIcon,
} from './icons'

/** Body building blocks shared by every skinned dialog. */

export function BrewSummary({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="brew-summary"
      className={cn(
        'flex flex-col rounded-[14px] bg-[var(--dlg-tile)] px-4 py-1',
        className
      )}
      {...props}
    />
  )
}

export function BrewSummaryRow({
  label,
  children,
  className,
}: {
  label: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 border-[var(--dlg-divider)] py-[11px] text-dialog-meta [&+&]:border-t',
        className
      )}
    >
      <span className="min-w-0 truncate text-[var(--dlg-body)]">{label}</span>
      <span className="min-w-0 truncate text-right font-medium text-[var(--dlg-ink)]">
        {children}
      </span>
    </div>
  )
}

export type BrewBannerTone = 'info' | 'warning' | 'error' | 'success'

const BANNER: Record<BrewBannerTone, { icon: LucideIcon; className: string }> =
  {
    info: {
      icon: InfoIcon,
      className: 'bg-[var(--dlg-tile)] text-[var(--dlg-body)]',
    },
    warning: {
      icon: AlertTriangleIcon,
      className: 'bg-[var(--dlg-warning-wash)] text-[var(--dlg-warning-ink)]',
    },
    error: {
      icon: AlertTriangleIcon,
      className: 'bg-[var(--dlg-danger-wash)] text-[var(--dlg-danger-ink)]',
    },
    success: {
      icon: CheckIcon,
      className: 'bg-[var(--dlg-success-wash)] text-[var(--dlg-success-ink)]',
    },
  }

/** One message in the body. Request errors live here, above the footer. */
export function BrewBanner({
  tone = 'info',
  children,
  className,
  role,
}: {
  tone?: BrewBannerTone
  children: ReactNode
  className?: string
  role?: 'alert' | 'status'
}) {
  const { icon: Icon, className: toneClass } = BANNER[tone]
  return (
    <div
      role={role ?? (tone === 'error' ? 'alert' : undefined)}
      className={cn(
        'flex items-start gap-2.5 rounded-xl px-3.5 py-3 text-dialog-meta leading-5',
        toneClass,
        className
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0">{children}</div>
    </div>
  )
}

/** Success replaces the body: a check, one line, one detail. */
export function BrewResult({
  title,
  description,
  icon: Icon = CheckIcon,
  tone = 'success',
}: {
  title: ReactNode
  description?: ReactNode
  icon?: LucideIcon
  tone?: 'success' | 'brand'
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-2xl bg-[var(--dlg-tile)] px-4 py-6 text-center">
      <span
        className={cn(
          'flex size-11 items-center justify-center rounded-full',
          tone === 'success'
            ? 'bg-[var(--dlg-success-wash)] text-[var(--dlg-success-ink)]'
            : 'bg-[var(--dlg-accent-wash)] text-[var(--dlg-accent-deep)]'
        )}
      >
        <Icon className="size-5" strokeWidth={2.4} aria-hidden="true" />
      </span>
      <span className="font-semibold text-[var(--dlg-ink)] text-dialog-lead">
        {title}
      </span>
      {description ? (
        <span className="text-[var(--dlg-body)] text-dialog-meta">
          {description}
        </span>
      ) : null}
    </div>
  )
}

export const BREW_LIST_FRAME_CLASS =
  'flex flex-col overflow-hidden rounded-[14px] bg-[var(--dlg-surface)] shadow-[0_0_0_1px_var(--dlg-line)]'

/**
 * A bordered list; rows are `BrewListRow`. `as="ul"`/`"ol"` for semantic
 * lists (then rows are `as="li"`), `footer` for a "+N more" line.
 */
export function BrewList({
  label,
  trailing,
  as: Tag = 'div',
  footer,
  className,
  children,
  ...props
}: Omit<ComponentProps<'div'>, 'children'> & {
  label?: ReactNode
  trailing?: ReactNode
  as?: 'div' | 'ul' | 'ol'
  footer?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-2">
      {label || trailing ? (
        <div className="flex items-center justify-between gap-3 text-dialog-meta">
          <span className="font-medium text-[var(--dlg-ink)]">{label}</span>
          {trailing ? (
            <span className="text-[var(--dlg-muted)] text-xs">{trailing}</span>
          ) : null}
        </div>
      ) : null}
      <div className={cn(BREW_LIST_FRAME_CLASS, className)} {...props}>
        <Tag className="flex min-h-0 flex-col overflow-y-auto">{children}</Tag>
        {footer}
      </div>
    </div>
  )
}

const ROW_BASE =
  'flex w-full gap-2.5 border-[var(--dlg-divider)] px-3.5 py-[11px] text-left [&+&]:border-t'

type BrewListRowContent = {
  leading?: ReactNode
  title: ReactNode
  description?: ReactNode
  /** Right side; muted caption text unless it is an element (badge, button). */
  trailing?: ReactNode
  /** A chevron at the far right of an interactive row. */
  chevron?: boolean
  selected?: boolean
  /** Center (default) or top-align the leading/trailing slots. */
  align?: 'center' | 'start'
  /** Let a long description wrap instead of truncating (server reasons). */
  wrapDescription?: boolean
  className?: string
  children?: ReactNode
}

function RowInner({
  leading,
  title,
  description,
  trailing,
  chevron,
  wrapDescription,
  children,
}: BrewListRowContent) {
  return (
    <>
      {leading}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[var(--dlg-ink)] text-sm leading-5">
          {title}
        </span>
        {description ? (
          <span
            className={cn(
              'text-[var(--dlg-muted)] text-xs leading-4',
              !wrapDescription && 'truncate'
            )}
          >
            {description}
          </span>
        ) : null}
        {children}
      </span>
      {typeof trailing === 'string' || typeof trailing === 'number' ? (
        <span className="shrink-0 text-[var(--dlg-muted)] text-xs">
          {trailing}
        </span>
      ) : (
        trailing
      )}
      {chevron ? (
        <ChevronRightIcon
          aria-hidden="true"
          className="size-4 shrink-0 text-[var(--dlg-faint)] transition-colors group-hover/row:text-[var(--dlg-ink)]"
        />
      ) : null}
    </>
  )
}

/**
 * One list row. A plain `div`/`li` by default; pass `onClick` for a button
 * row or `href` for an in-app link row (Next `Link`: client navigation),
 * with a hover wash, focus ring and optional chevron. With `as="li"` an
 * interactive row keeps list semantics: the `li` wraps the button / link.
 */
export function BrewListRow({
  as = 'div',
  onClick,
  href,
  disabled,
  selected,
  busy = false,
  align = 'center',
  className,
  dataAttributes,
  ...content
}: BrewListRowContent & {
  as?: 'div' | 'li'
  onClick?: () => void
  href?: string
  disabled?: boolean
  /** Working on this row's pick: a spinner replaces the chevron. */
  busy?: boolean
  /** `data-*` hooks on the row's button or link (tests, navigation probes). */
  dataAttributes?: Record<`data-${string}`, string | undefined>
}) {
  const rowClass = cn(
    ROW_BASE,
    align === 'start' ? 'items-start' : 'items-center',
    selected && 'bg-[var(--dlg-accent-wash)]',
    className
  )
  const interactiveClass =
    'group/row outline-none transition-colors hover:bg-[var(--dlg-accent-wash)] focus-visible:bg-[var(--dlg-accent-wash)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent'
  const inner = busy ? (
    <RowInner
      {...content}
      chevron={false}
      trailing={
        <Spinner className="size-3.5 shrink-0 text-[var(--dlg-muted)]" />
      }
    />
  ) : (
    <RowInner {...content} />
  )
  let interactive: ReactNode = null
  if (href) {
    interactive = (
      <Link
        href={href}
        className={cn(rowClass, interactiveClass)}
        {...dataAttributes}
      >
        {inner}
      </Link>
    )
  } else if (onClick) {
    interactive = (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-pressed={selected}
        aria-busy={busy || undefined}
        className={cn(rowClass, interactiveClass)}
        {...dataAttributes}
      >
        {inner}
      </button>
    )
  }
  if (interactive && as === 'li') {
    return (
      <li className="border-[var(--dlg-divider)] [&+&]:border-t [&>*]:border-t-0">
        {interactive}
      </li>
    )
  }
  if (interactive) {
    return interactive
  }
  const Tag = as
  return <Tag className={rowClass}>{inner}</Tag>
}

/** "and N more" under a capped list, in the reader's language. */
export function BrewListMore({
  count,
  className,
}: {
  count: number
  className?: string
}) {
  const t = useTranslations('ui')
  if (count <= 0) {
    return null
  }
  return (
    <div
      className={cn(
        'border-[var(--dlg-divider)] border-t bg-[var(--dlg-tile)] px-3.5 py-2.5 text-[var(--dlg-muted)] text-xs',
        className
      )}
    >
      {t('brewDialog.listMore', { count })}
    </div>
  )
}

/** Up to `limit` items and how many were left out — for inline "+N more". */
export function previewList<T>(
  items: ReadonlyArray<T>,
  limit: number
): { shown: Array<T>; rest: number } {
  return {
    shown: items.slice(0, limit),
    rest: Math.max(0, items.length - limit),
  }
}

/** Nothing to show yet: an optional icon, one line, one detail, an action. */
export function BrewEmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: {
  icon?: LucideIcon
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-1.5 rounded-[14px] bg-[var(--dlg-tile)] px-6 py-8 text-center',
        className
      )}
    >
      {Icon ? (
        <Icon
          aria-hidden="true"
          className="mb-1 size-5 text-[var(--dlg-muted)]"
        />
      ) : null}
      <span className="font-medium text-[var(--dlg-ink)] text-sm">{title}</span>
      {description ? (
        <span className="max-w-[40ch] text-[var(--dlg-muted)] text-xs leading-[17px]">
          {description}
        </span>
      ) : null}
      {action ? <div className="pt-2">{action}</div> : null}
    </div>
  )
}

/** A media preview on the dialog's soft tile (lightbox, attachments). */
export function BrewMediaFrame({
  tone = 'light',
  bare = false,
  className,
  children,
}: {
  tone?: 'light' | 'dark'
  /** An editing stage: images show their exact pixels (no rounding/shadow). */
  bare?: boolean
  className?: string
  children: ReactNode
}) {
  return (
    <div
      className={cn(
        'flex justify-center rounded-2xl p-3',
        !bare &&
          '[&_img]:rounded-[14px] [&_img]:shadow-[var(--dlg-lift-media)]',
        tone === 'dark' ? 'bg-[var(--dlg-viewer-bg)]' : 'bg-[var(--dlg-tile)]',
        className
      )}
    >
      {children}
    </div>
  )
}

/** Tinted pill badge in the dialog palette. */
export function BrewBadge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: 'neutral' | 'brand' | 'success' | 'danger' | 'warning'
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex h-[22px] shrink-0 items-center whitespace-nowrap rounded-full px-[9px] font-medium text-xs',
        {
          'bg-[var(--dlg-tile)] text-[var(--dlg-body)] shadow-[inset_0_0_0_1px_var(--dlg-line)]':
            tone === 'neutral',
          'bg-[var(--dlg-accent-wash)] text-[var(--dlg-accent-deep)]':
            tone === 'brand',
          'bg-[var(--dlg-success-wash)] text-[var(--dlg-success-ink)]':
            tone === 'success',
          'bg-[var(--dlg-danger-wash)] text-[var(--dlg-danger-ink)]':
            tone === 'danger',
          'bg-[var(--dlg-warning-wash)] text-[var(--dlg-warning-ink)]':
            tone === 'warning',
        },
        className
      )}
    >
      {children}
    </span>
  )
}

/** Gradient progress bar with a caption row. */
export function BrewProgress({
  value,
  label,
  trailing,
  tone = 'brand',
  indeterminate = false,
  ariaLabel,
}: {
  value: number
  label?: ReactNode
  trailing?: ReactNode
  tone?: 'brand' | 'danger' | 'success'
  /** Still working with no measurable progress (a sweeping bar). */
  indeterminate?: boolean
  /** The bar's name when it has no visible `label` ("Progress" by default). */
  ariaLabel?: string
}) {
  const t = useTranslations('ui')
  const labelId = useId()
  return (
    <div className="flex flex-col gap-2.5">
      <div
        role="progressbar"
        aria-label={label ? undefined : (ariaLabel ?? t('brewDialog.progress'))}
        aria-labelledby={label ? labelId : undefined}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={indeterminate ? undefined : Math.round(value)}
        className="h-2 overflow-hidden rounded-full bg-[var(--dlg-divider)]"
      >
        <div
          className={cn('h-2 rounded-full transition-[width] duration-300', {
            'bg-[linear-gradient(90deg,var(--dlg-accent),var(--dlg-accent-deep))]':
              tone === 'brand',
            'bg-[var(--dlg-danger)]': tone === 'danger',
            'bg-[var(--dlg-success-ink)]': tone === 'success',
            'w-1/3 animate-pulse': indeterminate,
          })}
          style={
            indeterminate
              ? undefined
              : { width: `${Math.max(0, Math.min(100, value))}%` }
          }
        />
      </div>
      {label || trailing ? (
        <div className="flex items-center justify-between gap-3 text-dialog-meta">
          <span id={labelId} className="font-medium text-[var(--dlg-ink)]">
            {label}
          </span>
          {trailing ? <span className="flex gap-3">{trailing}</span> : null}
        </div>
      ) : null}
    </div>
  )
}

/** Big-number tiles (results). */
export function BrewStats({
  items,
}: {
  items: ReadonlyArray<{
    value: ReactNode
    label: ReactNode
    tone?: 'neutral' | 'success' | 'warning' | 'danger'
  }>
}) {
  return (
    <div className="flex gap-2">
      {items.map((item, index) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: static tiles
          key={index}
          className={cn(
            'flex min-w-0 flex-1 flex-col gap-0.5 rounded-[14px] px-4 py-3.5',
            {
              'bg-[var(--dlg-tile)] text-[var(--dlg-ink)]':
                (item.tone ?? 'neutral') === 'neutral',
              'bg-[var(--dlg-success-wash)] text-[var(--dlg-success-ink)]':
                item.tone === 'success',
              'bg-[var(--dlg-warning-wash)] text-[var(--dlg-warning-ink)]':
                item.tone === 'warning',
              'bg-[var(--dlg-danger-wash)] text-[var(--dlg-danger-ink)]':
                item.tone === 'danger',
            }
          )}
        >
          <span className="font-semibold text-2xl tabular-nums tracking-[-0.02em]">
            {item.value}
          </span>
          <span className="text-[var(--dlg-body)] text-xs">{item.label}</span>
        </div>
      ))}
    </div>
  )
}
